import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import Card from "../../../components/Card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import Loader from "../../../components/partials/components/loader";
import { getApiCall } from "../../../service/getApiCall";
import { postApiCall } from "../../../service/postApiCall";
import {
  API_MANAGE_LOCATIONS_LIST, // GET /vX/locations
  API_COMPANY_LIST,            // GET /vX/Company
  API_BULK_LOCATION_DELETE,  // POST /vX/locations/bulk-delete  (add in url.js)
  API_GET_COMPANY_LOCATION
} from "../../../service/url";
import { toast } from 'react-toastify';

export default function BulkLocationDelete() {
  const [loading, setLoading] = useState(false);

  // master lists
  const [locations, setLocations] = useState([]);   // [{location_id, location_name}]
  const [allCompany, setAllCompany] = useState([]);     // full Company list from API

  // working (filtered) list shown in table
  const [company, setCompany] = useState([]);

  // UI state
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [rows, setRows] = useState(10);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [locationName, setLocationName] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [locRes, companyRes] = await Promise.all([
          getApiCall(API_MANAGE_LOCATIONS_LIST, {}),
          getApiCall(API_COMPANY_LIST, {}),
        ]);

        setLocations(Array.isArray(locRes) ? locRes : []);
        setAllCompany(Array.isArray(companyRes) ? companyRes : []);
        setCompany([]);
      } catch (err) {
        toast.error("Failed to load Dialer locations/company");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Filter button: show only Company who belong to the selected location
  const applyLocationFilter = async () => {
    if (!selectedLocationId) {
      toast.error("Please select a Dialer location first.");
      return;
    }

    const lid = String(selectedLocationId);
    try {
      const locationCompany = await getApiCall(
        API_GET_COMPANY_LOCATION(lid),
        {}
      );

      const companyIds = locationCompany.map(item => Number(item.company_id));

      const companyIdSet = new Set(companyIds);

      const filtered = (allCompany || []).filter(company =>
        companyIdSet.has(Number(company.id))
      );

      const location = (locations || []).find(
        (loc) => Number(loc.location_id) === Number(selectedLocationId)
      );

      setLocationName(location?.location_name)
      setCompany(filtered);
      setSelectedCompanyIds([]);
    } catch (error) {
      console.error("Error fetching company Dialer location", error);
    }
  };


  
  const clearFilter = () => {
    setCompany([]);
    setSelectedCompanyIds([]);
    setSearchValue("");
  };

  // Search within the filtered list
  const searched = useMemo(
    () =>
      (company || []).filter((u) =>
        (u.name || "").toLowerCase().includes(searchValue.toLowerCase())
      ),
    [company, searchValue]
  );

  const toggleCompany = (companyId) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  };

  // Select-all respects current search
  const toggleAll = () => {
    const visibleIds = searched.map((u) => u.id);
    const allSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedCompanyIds.includes(id));

    if (allSelected) {
      setSelectedCompanyIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedCompanyIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedLocationId) {
      toast.error("Please select a Dialer location first.");
      return;
    }
    if (selectedCompanyIds.length === 0) {
      toast.error("Select at least one company.");
      return;
    }

    try {
      setButtonDisable(true);
      const payload = { company_ids: selectedCompanyIds, location_id: selectedLocationId };
      const res = await postApiCall(API_BULK_LOCATION_DELETE, payload);

      if (res) {
        toast.success(res.message || "company removed from Dialer location successfully");
        applyLocationFilter(); // refresh list
        setSelectedCompanyIds([]);
      } else {
        toast.error(res?.message || "Failed to remove company");
      }
    } catch (err) {
      toast.error(err?.message || "Error removing company from Dialer location");
    } finally {
      setButtonDisable(false);
      setConfirmVisible(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div >
      <Row style={{marginTop:"20px"}}>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="card-title">Bulk Company Dialer Location Deletion</h4>
            </Card.Header>

            <Card.Body className="px-0">
              {/* Controls Row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px",
                  flexWrap: "wrap",
                }}
              >
                {/* Show X items */}
                <span>Show</span>
                <Form.Select
  value={rows}
  onChange={(e) => setRows(Number(e.target.value))}
  style={{
    width: 90,
    height: 40,
    display: "flex",
    alignItems: "center",
  }}
>
  {[
    { label: "5", value: 5 },
    { label: "10", value: 10 },
    { label: "20", value: 20 },
    { label: "50", value: 50 },
  ].map((option) => (
    <option
      key={option.value}
      value={option.value}
    >
      {option.label}
    </option>
  ))}
</Form.Select>
                <span>items</span>

                {/* Search */}
                <Form.Control
                  style={{ width: 250, height: 40, marginLeft: 10 }}
                  placeholder="Search"
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />

                <Button label="Clear" className="p-button-secondary" onClick={clearFilter} />

                {/* Location selector + Filter */}
                <Form.Select
  value={selectedLocationId || ""}
  onChange={(e) =>
    setSelectedLocationId(e.target.value || null)
  }
  style={{
    width: 260,
    marginLeft: 10,
  }}
>
  <option value="">Select Dialer Location</option>

  {(locations || []).map((l) => (
    <option
      key={l.location_id}
      value={l.location_id}
    >
      {l.location_name}
    </option>
  ))}
</Form.Select>
                <Button label="Filter" className="p-button-secondary" onClick={applyLocationFilter} />

                {/* Delete Selected */}
                <Button
                  label="Delete Selected"
                  className="p-button-danger"
                  disabled={selectedCompanyIds.length === 0 || !selectedLocationId || buttonDisable}
                  onClick={() => setConfirmVisible(true)}
                />
              </div>

              {/* Table */}
              <div className="scroll-container">
                <DataTable
                  value={searched}
                  paginator
                  rows={rows}
                  tableStyle={{ minWidth: "50rem" }}
                  emptyMessage="No data available"
                >
                  <Column
                    header={
                      <Checkbox
                        checked={
                          searched.length > 0 &&
                          searched.every((u) => selectedCompanyIds.includes(u.id))
                        }
                        onChange={toggleAll}
                      />
                    }
                    headerStyle={{ width: "3rem" }}
                    body={(row) => (
                      <Checkbox
                        checked={selectedCompanyIds.includes(row.id)}
                        onChange={() => toggleCompany(row.id)}
                      />
                    )}
                  />
                  <Column field="id" header="Company Id" sortable />
                  <Column field="name" header="Company Name" sortable />
                  <Column header="Dialer Location Name" body={locationName} sortable />
                </DataTable>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirm Modal */}
      <Dialog
        header="Confirm Bulk Delete"
        visible={confirmVisible}
        style={{ width: "25vw" }}
        onHide={() => setConfirmVisible(false)}
        footer={
          <div>
            <Button label="Cancel" className="p-button-text" onClick={() => setConfirmVisible(false)} />
            <Button label="Confirm" className="p-button-danger" onClick={handleBulkDelete} disabled={buttonDisable} />
          </div>
        }
      >
        <p>Are you sure you want to remove {selectedCompanyIds.length} Company(s) from this Dialer location?</p>
      </Dialog>
    </div>
  );
}
